import React, { useEffect, useRef, useState } from 'react';
import '../cmpMemberPages.css';
import { Dialog } from '@material-ui/core';
import {
  CmpEventCustomFieldTypeEnum as FieldType,
  CmpMyMemberEventRegistrationFragment,
  useGetMyCmpMemberEventsQuery,
  useUpdateMyCmpMemberEventAttendeesMutation,
} from '../../../../graphql/types';
import useMemberDashboardAccess from '../../../common/Hooks/useMemberDashboardAccess';

// Wave C ACON-5 (Member Dashboard "Register for Events" ACC:L1025-1078,
// Attendee Details ACC:L1434-1497, past Attendees ACC:L1499-1514; M8, W14).

type Registration = CmpMyMemberEventRegistrationFragment;
type Question = Registration['questions'][number];
type Draft = { id: string; name: string; email: string; ticketTypeId: string; answers: Record<string, string>; originalName: string;
  firstName: string; lastName: string | null };

const GOLD = '#E1A731';
const RED = '#ED1C24';
const GREEN = '#10B981';
const TEXT = '#000000';
const BLUE = '#0D8BF2';
const BLACK_900 = '#1A1A1A';
const ACCOMMODATIONS = 'question-accommodations';
const DIETARY = 'question-dietary';
export const ATTENDEE_SAVE_ERROR = 'Attendee 1 needs a name and valid email. Every other attendee needs a name, and any email entered must be valid.';
const operationContext = { preserveErrorForCaller: true };
const validEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());
const errorMessage = (error: unknown, fallback: string) =>
  (error as { graphQLErrors?: { message?: string }[] }).graphQLErrors?.[0]?.message || fallback;

const inZone = (utcMilli: number, timeZone: string, options: Intl.DateTimeFormatOptions) => {
  try { return new Intl.DateTimeFormat('en-US', { ...options, timeZone }).format(utcMilli); }
  catch { return new Intl.DateTimeFormat('en-US', options).format(utcMilli); }
};
export const formatEventWhen = (row: Pick<Registration, 'startAtUtcMilli' | 'endAtUtcMilli' | 'timeZone'>): string => {
  if (!row.startAtUtcMilli) return 'Date to be confirmed';
  const day = inZone(row.startAtUtcMilli, row.timeZone, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const time = (value: number) => inZone(value, row.timeZone, { hour: 'numeric', minute: '2-digit' });
  if (!row.endAtUtcMilli) return `${day} · ${time(row.startAtUtcMilli)}`;
  const [startTime, endTime] = [time(row.startAtUtcMilli), time(row.endAtUtcMilli)];
  const sameMeridiem = startTime.slice(-2) === endTime.slice(-2);
  return `${day} · ${sameMeridiem ? startTime.slice(0, -3) : startTime} – ${endTime}`;
};
const money = (cents: number, currency: string) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency || 'CAD' }).format(cents / 100);
const fullName = (first: string, last?: string | null) => [first, last].map(value => (value || '').trim()).filter(Boolean).join(' ');
const splitName = (name: string, draft: Draft) => {
  const trimmed = name.trim().replace(/\s+/g, ' ');
  if (trimmed === draft.originalName) return { firstName: draft.firstName, lastName: draft.lastName };
  const parts = trimmed.split(' ');
  return parts.length > 1 ? { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] } : { firstName: trimmed, lastName: null };
};
const questionsFor = (row: Registration, ticketTypeId: string) =>
  row.questions.filter(question => !question.appliesToTicketTypeIds.length || question.appliesToTicketTypeIds.includes(ticketTypeId));
export const isDraftComplete = (row: Registration, draft: Draft) => !!draft.name.trim() && validEmail(draft.email) &&
  questionsFor(row, draft.ticketTypeId).every(question => !question.required || !!(draft.answers[question.id] || '').trim());

const pillCard = (title: string, children: React.ReactNode) => (
  <div className="cmp-ev-pillcard" style={{ position: 'relative', border: '1px solid #CFCFCF', borderRadius: 12, padding: '44px 28px 28px' }}>
    <div style={{ position: 'absolute', top: -19, left: 16, background: GOLD, color: '#FFFFFF', fontSize: 14, fontWeight: 600, padding: '9px 22px', borderRadius: 999 }}>{title}</div>
    {children}
  </div>
);
const CloseButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
  <button type="button" aria-label="Close" disabled={disabled} onClick={onClick} style={{ position: 'absolute', top: 14, right: 14, width: 28, height: 28,
    borderRadius: '50%', background: RED, color: '#FFFFFF', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>✕</button>
);
const inputStyle = (bad: boolean): React.CSSProperties => ({ border: `1px solid ${bad ? RED : '#CFCFCF'}`, borderRadius: 8, padding: '10px 12px',
  fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: TEXT, width: '100%', boxSizing: 'border-box', outline: 'none', background: '#FFFFFF' });

/** MD-E12 (ACC:1463-1478): design drop-down, gold-free, green check on the picked option. */
const CmpChoiceDropdown = ({ value, placeholder, options, onChange, ariaLabel }: {
  value: string; placeholder: string; options: { value: string; label: string }[]; onChange: (value: string) => void; ariaLabel: string;
}) => {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => { if (box.current && !box.current.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const picked = options.find(option => option.value && option.value === value);
  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button type="button" aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#FFFFFF',
          border: `1px solid ${open ? BLACK_900 : '#CFCFCF'}`, borderRadius: 8, padding: '10px 12px', fontFamily: "'DM Sans', sans-serif", fontSize: 14,
          color: picked ? TEXT : '#8A8A8A', cursor: 'pointer', textAlign: 'left', boxSizing: 'border-box' }}>
        <span>{picked ? picked.label : placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEXT} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          style={{ flexShrink: 0, transition: 'transform 150ms ease', transform: open ? 'rotate(180deg)' : 'none' }}><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div role="listbox" aria-label={ariaLabel} style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 2, background: '#FFFFFF',
          border: '1px solid #CFCFCF', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.10)', padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {options.map(option => {
            const selected = (value || '') === option.value;
            return (
              <button key={option.value || 'none'} type="button" role="option" aria-selected={selected} className="cmp-ev-option"
                onClick={() => { onChange(option.value); setOpen(false); }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', background: selected ? '#F5F9FD' : '#FFFFFF',
                  border: 'none', borderRadius: 6, padding: '10px 12px', fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: selected ? 600 : 400,
                  color: TEXT, cursor: 'pointer', textAlign: 'left' }}>
                <span>{option.label}</span><span style={{ color: GREEN, fontWeight: 700 }}>{selected ? '✓' : ''}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// MD-E10 (ACC:1452-1453): question heading 14px/700, helper 13px black.
const questionHeading = (title: React.ReactNode, helper?: React.ReactNode) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>{title}</div>
    {helper ? <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.5 }}>{helper}</div> : null}
  </div>
);

const QuestionField = ({ question, value, onChange }: { question: Question; value: string; onChange: (value: string) => void }) => {
  const selected = value.split(',').map(item => item.trim()).filter(Boolean);
  if (question.fieldType === FieldType.MultiSelect || (question.fieldType === FieldType.Checkbox && question.choices.length > 0)) {
    const accommodations = question.id === ACCOMMODATIONS;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {questionHeading(accommodations ? 'Do you have any special accommodations?' : question.label,
          accommodations ? 'Let us know if you have any accessibility or special accommodation requirements.' : question.helperText)}
        {question.choices.map(choice => (
          <label key={choice} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, cursor: 'pointer' }}>
            {/* MD-E11 (ACC:1455, 2712): design 20px check box, radius 5, green tick. */}
            <input type="checkbox" className="cmp-check" checked={selected.includes(choice)}
              style={{ width: 20, height: 20, minWidth: 20, margin: 0, borderRadius: 5, borderColor: selected.includes(choice) ? GREEN : '#CFCFCF' }}
              onChange={() => onChange(question.choices.filter(item => item === choice ? !selected.includes(choice) : selected.includes(item)).join(', '))} />
            <span style={{ color: TEXT }}>{choice}</span>
          </label>
        ))}
      </div>
    );
  }
  if (question.fieldType === FieldType.Select) {
    const dietary = question.id === DIETARY;
    // MD-E13 (ACC:1472, 2763): the chamber's own question text stays; a meal question uses the design "Select meal".
    const meal = /meal/i.test(question.label);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {questionHeading(dietary ? 'If you have a dietary preference, select from the available options' : question.label,
          dietary ? 'Do not select if no preference is required' : question.helperText)}
        <CmpChoiceDropdown value={selected[0] || ''} onChange={onChange} ariaLabel={dietary ? 'Dietary option' : question.label}
          placeholder={dietary ? 'Select a dietary option' : meal ? 'Select meal' : 'Select an option'}
          options={[...(dietary ? [{ value: '', label: 'No dietary option' }] : []), ...question.choices.map(choice => ({ value: choice, label: choice }))]} />
      </div>
    );
  }
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 14, fontWeight: 700 }}>{question.label}{question.required && <span style={{ color: RED }}> *</span>}</span>
      {question.fieldType === FieldType.Textarea
        ? <textarea rows={3} value={value} onChange={event => onChange(event.target.value)} style={{ ...inputStyle(false), resize: 'vertical' }} />
        : <input value={value} onChange={event => onChange(event.target.value)} style={inputStyle(false)} />}
    </label>
  );
};

const AttendeeDetailsModal = ({ row, onClose, onSaved }: { row: Registration; onClose: () => void; onSaved: () => void }) => {
  const [save, { loading }] = useUpdateMyCmpMemberEventAttendeesMutation({ context: operationContext });
  const [drafts, setDrafts] = useState<Draft[]>(() => row.attendees.map(attendee => ({
    id: attendee.id, name: fullName(attendee.firstName, attendee.lastName), email: attendee.email || '', ticketTypeId: attendee.ticketTypeId,
    answers: Object.fromEntries(attendee.answers.map(answer => [answer.fieldId, answer.value])),
    originalName: fullName(attendee.firstName, attendee.lastName), firstName: attendee.firstName, lastName: attendee.lastName || null,
  })));
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const setDraft = (index: number, patch: Partial<Draft>) => {
    setDrafts(current => current.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)));
    setShowErrors(false); setSaveError(undefined);
  };
  const rowInvalid = (draft: Draft, index: number) => ({
    name: !draft.name.trim(),
    email: (index === 0 && !validEmail(draft.email)) || (!!draft.email.trim() && !validEmail(draft.email)),
  });
  const submit = async () => {
    if (drafts.some((draft, index) => { const bad = rowInvalid(draft, index); return bad.name || bad.email; })) { setShowErrors(true); return; }
    try {
      await save({ variables: { input: { registrationId: row.registrationId, expectedUpdatedAtUtcMilli: row.updatedAtUtcMilli,
        attendees: drafts.map(draft => ({ id: draft.id, ...splitName(draft.name, draft), email: draft.email.trim() || null,
          answers: questionsFor(row, draft.ticketTypeId).map(question => ({ fieldId: question.id, value: (draft.answers[question.id] || '').trim() }))
            .filter(answer => !!answer.value) })) } } });
      onSaved();
    } catch (error) {
      setSaveError(errorMessage(error, 'Attendee details could not be saved. Refresh the page and try again.'));
    }
  };
  return (
    // MD-E6 (ACC:1429): only the ✕ closes Attendee Details.
    <Dialog open maxWidth={false} aria-labelledby="ev-att-title"
      PaperProps={{ style: { width: 'min(720px, 94vw)', maxHeight: '90vh', borderRadius: 16, margin: 0, position: 'relative', display: 'flex', flexDirection: 'column',
        fontFamily: "'DM Sans', sans-serif", color: TEXT } }}>
      <CloseButton onClick={onClose} disabled={loading} />
      <div style={{ padding: '26px 32px 16px', borderBottom: '1px solid #ECECEC' }}>
        <div id="ev-att-title" style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Attendee Details</div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{row.eventTitle}</div>
        <div style={{ fontSize: 13, marginTop: 2 }}>{formatEventWhen(row)}{row.locationText ? ` · ${row.locationText}` : ''}</div>
        <div style={{ fontSize: 13, marginTop: 2 }}>{row.ticketSummary} · {row.ticketCount} ticket(s)</div>
      </div>
      <div style={{ padding: '20px 32px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {drafts.map((draft, index) => {
          const bad = showErrors ? rowInvalid(draft, index) : { name: false, email: false };
          const complete = isDraftComplete(row, draft);
          const questions = questionsFor(row, draft.ticketTypeId);
          return (
            <div key={draft.id} data-testid={`attendee-${index}`} style={{ border: '1px solid #ECECEC', borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{`Attendee ${index + 1}${index === 0 ? ' (registered at purchase)' : ''}`}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: complete ? GREEN : RED }}>{complete ? 'Complete' : 'Missing details'}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <label><div style={{ fontSize: 13, marginBottom: 6 }}>Attendee name <span style={{ color: RED }}>*</span></div>
                  <input value={draft.name} onChange={event => setDraft(index, { name: event.target.value })} placeholder={index === 0 ? 'Full name' : `Attendee ${index + 1}`} style={inputStyle(bad.name)} /></label>
                <label><div style={{ fontSize: 13, marginBottom: 6 }}>Email {index === 0 && <span style={{ color: RED }}>*</span>}</div>
                  <input type="email" value={draft.email} onChange={event => setDraft(index, { email: event.target.value })} placeholder="name@company.com" style={inputStyle(bad.email)} /></label>
              </div>
              {/* MD-E9 (ACC:1449): questions side by side. */}
              {questions.length > 0 && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, alignItems: 'start' }}>
                {questions.map(question => <QuestionField key={question.id} question={question} value={draft.answers[question.id] || ''}
                  onChange={value => setDraft(index, { answers: { ...draft.answers, [question.id]: value } })} />)}
              </div>}
            </div>
          );
        })}
      </div>
      {/* MD-E2 (ACC:1485): light blue footer, no border. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '16px 32px 20px', background: '#F5F9FD', borderRadius: '0 0 16px 16px' }}>
        {showErrors && <span role="alert" style={{ flexBasis: '100%', fontSize: 13, fontWeight: 600, color: RED }}>{ATTENDEE_SAVE_ERROR}</span>}
        {saveError && <span role="alert" style={{ flexBasis: '100%', fontSize: 13, fontWeight: 600, color: RED }}>{saveError}</span>}
        <button type="button" onClick={submit} disabled={loading} style={{ flex: 1, background: BLACK_900, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '13px 0',
          fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          Save Attendee Details
        </button>
      </div>
    </Dialog>
  );
};

const PastAttendeesModal = ({ row, onClose }: { row: Registration; onClose: () => void }) => (
  <Dialog open onClose={onClose} maxWidth={false} aria-labelledby="ev-past-title"
    PaperProps={{ style: { width: 'min(560px, 94vw)', maxHeight: '88vh', overflowY: 'auto', borderRadius: 16, margin: 0, position: 'relative', padding: '28px 32px 28px', fontFamily: "'DM Sans', sans-serif", color: TEXT } }}>
    <CloseButton onClick={onClose} />
    <div id="ev-past-title" style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Attendees</div>
    <div style={{ fontSize: 14, fontWeight: 600 }}>{row.eventTitle}</div>
    <div style={{ fontSize: 13, margin: '2px 0 20px' }}>
      {row.startAtUtcMilli ? inZone(row.startAtUtcMilli, row.timeZone, { month: 'short', day: '2-digit', year: 'numeric' }) : ''}{row.locationText ? ` · ${row.locationText}` : ''} · {row.ticketCount} ticket(s)
    </div>
    {/* MD-E19 (ACC:1499-1501) */}
    <div style={{ display: 'grid', gridTemplateColumns: '28px minmax(0,1fr) minmax(0,1.3fr)', gap: 12, padding: '10px 12px', fontSize: 12, fontWeight: 600,
      letterSpacing: '0.04em', color: '#000000', background: '#F7F7F8', borderBottom: '1px solid #ECECEC' }}>
      <span>#</span><span>Name</span><span>Email</span>
    </div>
    {row.attendees.map((attendee, index) => (
      <div key={attendee.id} style={{ display: 'grid', gridTemplateColumns: '28px minmax(0,1fr) minmax(0,1.3fr)', gap: 12, padding: 12, fontSize: 13, borderTop: '1px solid #ECECEC' }}>
        <span>{index + 1}</span><span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{fullName(attendee.firstName, attendee.lastName)}</span>
        <span style={{ overflowWrap: 'anywhere' }}>{attendee.email || '—'}</span>
      </div>
    ))}
  </Dialog>
);

export function MemberEventsView({ chamberName, eventsUrl, upcoming, past, loading, error, onRetry, onEdit, onViewPast }: {
  chamberName: string; eventsUrl?: string | null; upcoming: Registration[]; past: Registration[]; loading: boolean; error?: string;
  onRetry: () => void; onEdit: (row: Registration) => void; onViewPast: (row: Registration) => void;
}) {
  const missingRows = upcoming.filter(row => row.missingAttendeeCount > 0).length;
  const unpaidRows = upcoming.filter(row => !row.paid).length;
  return (
    // Own page background like the other member pages, so the shell never shows through.
    <main style={{ paddingBottom: 48, fontFamily: "'DM Sans', sans-serif", color: TEXT, background: '#F7F7F8', minHeight: '100vh' }}>
      <div className="cmp-ev-heading" style={{ background: GOLD, padding: '28px 40px 100px', borderRadius: '0 0 18px 18px' }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, color: '#FFFFFF', margin: '0 0 8px' }}>Register for Events</h1>
        <div style={{ fontSize: 15, color: '#FFFFFF' }}>Register for {chamberName} events and keep attendee details up to date.</div>
      </div>
      <div className="cmp-ev-body" style={{ background: '#FFFFFF', borderRadius: 18, margin: '-72px 28px 0', padding: 40, display: 'flex', flexDirection: 'column', gap: 40 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ fontSize: 14, lineHeight: 1.55, maxWidth: 520 }}>Select button to review event calendar to select an event to register for</div>
          {/* MD-E21 (ACC:1028): hover turns the button blue. */}
          {eventsUrl && <a href={eventsUrl} target="_blank" rel="noopener noreferrer" className="cmp-ev-register" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#FFFFFF',
            borderRadius: 8, padding: '13px 24px', fontSize: 15, fontWeight: 600, textDecoration: 'none' }}>
            Register for Event<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8" /></svg>
          </a>}
        </div>
        {pillCard('My Registered Events', loading ? <p role="status">Loading your events…</p> : error ? (
          <div role="alert"><p style={{ color: RED, fontWeight: 600 }}>{error}</p><button type="button" onClick={onRetry} style={{ background: BLUE, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer' }}>Try again</button></div>
        ) : <>
          {(missingRows > 0 || unpaidRows > 0) && <div style={{ fontSize: 14, marginBottom: 20 }}>
            You are registered for <b>{upcoming.length}</b> events. <b style={{ color: RED }}>{missingRows}</b> event(s) are missing attendee details. <b style={{ color: RED }}>{unpaidRows}</b> event(s) have tickets that have not been paid.
          </div>}
          {!upcoming.length && <p style={{ fontSize: 14 }}>You have no upcoming event registrations.</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {upcoming.map(row => {
              const missing = row.missingAttendeeCount;
              return (
                // Wraps on narrow screens (date, details, then the button) so the
                // title never shrinks to one letter per line; one row on desktop.
                <div key={row.registrationId} data-testid={`event-${row.registrationId}`} className="cmp-ev-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 20, rowGap: 14, alignItems: 'center',
                  border: `1px solid ${missing || !row.paid ? '#F5B5B8' : '#ECECEC'}`, borderRadius: 12, padding: '18px 20px' }}>
                  <div style={{ flex: '0 0 72px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid #ECECEC', borderRadius: 10, padding: '10px 0' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', color: '#C6912A' }}>{row.startAtUtcMilli ? inZone(row.startAtUtcMilli, row.timeZone, { month: 'short' }).toUpperCase() : 'TBC'}</span>
                    <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>{row.startAtUtcMilli ? inZone(row.startAtUtcMilli, row.timeZone, { day: '2-digit' }) : '—'}</span>
                  </div>
                  <div data-testid={`event-details-${row.registrationId}`} style={{ flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
                    <span style={{ fontSize: 16, fontWeight: 600, overflowWrap: 'anywhere' }}>{row.eventTitle}</span>
                    <span style={{ fontSize: 13 }}>{formatEventWhen(row)}{row.locationText ? ` · ${row.locationText}` : ''}</span>
                    <span style={{ fontSize: 13 }}>{row.ticketSummary} · <b>{row.ticketCount} ticket(s)</b>{row.registrationNumber ? ` · Order #${row.registrationNumber}` : ''}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: missing ? RED : GREEN }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: missing ? RED : GREEN }} />
                      {missing ? `Attendee details missing for ${missing} of ${row.ticketCount} ticket(s)` : 'All attendee details complete'}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: row.paid ? GREEN : RED }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: row.paid ? GREEN : RED }} />
                      {row.paid ? `Tickets paid (${money(row.amountInCents, row.currency)})` : `Tickets not paid · ${money(row.amountDueInCents, row.currency)} due`}
                    </span>
                  </div>
                  <button type="button" disabled={!row.canEditAttendees} onClick={() => onEdit(row)} style={{ flex: '0 0 auto', marginLeft: 'auto', background: missing ? TEXT : '#FFFFFF', color: missing ? '#FFFFFF' : TEXT,
                    border: missing ? 'none' : '1px solid #CFCFCF', borderRadius: 8, padding: '10px 18px', fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
                    cursor: row.canEditAttendees ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', opacity: row.canEditAttendees ? 1 : 0.5 }}>
                    {missing ? 'Add Attendee Details' : 'Update Attendees'}
                  </button>
                </div>
              );
            })}
          </div>
        </>)}
        {/* MD-E16 (ACC:1054-1055) */}
        {pillCard('Past Event History', <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 680 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '105px minmax(150px,1fr) 115px 75px 90px 85px', gap: 12, alignItems: 'center', padding: '10px 12px', fontSize: 13, fontWeight: 600,
            letterSpacing: '0.04em', color: '#000000', background: '#F7F7F8', borderBottom: '1px solid #ECECEC' }}>
            <span>Date</span><span>Event</span><span>Ticket Type</span><span>Ticket Qty</span><span>Attendees</span><span>Amount</span>
          </div>
          {!loading && !past.length && <p style={{ fontSize: 14, padding: '10px 12px' }}>No past events yet.</p>}
          {past.map(row => (
            <div key={row.registrationId} style={{ display: 'grid', gridTemplateColumns: '105px minmax(150px,1fr) 115px 75px 90px 85px', gap: 12, alignItems: 'center',
              padding: 12, borderTop: '1px solid #ECECEC', fontSize: 13 }}>
              <span>{row.startAtUtcMilli ? inZone(row.startAtUtcMilli, row.timeZone, { month: 'short', day: '2-digit', year: 'numeric' }) : '—'}</span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}><span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{row.eventTitle}</span>
                <span style={{ fontSize: 12 }}>{row.locationText}</span></span>
              <span>{row.ticketSummary}</span>
              <span>{row.ticketCount}</span>
              <span><button type="button" onClick={() => onViewPast(row)} style={{ background: BLACK_900, color: '#FFFFFF', border: `1px solid ${BLACK_900}`, borderRadius: 8, padding: '7px 18px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600 }}>View</button></span>
              <span style={{ fontWeight: 600 }}>{money(row.amountInCents, row.currency)}</span>
            </div>
          ))}
        </div></div>)}
      </div>
    </main>
  );
}

export default function MemberEvents() {
  const { user } = useMemberDashboardAccess();
  return <MemberEventsPage chamberName={user?.chamberInfo.name || 'your chamber'} />;
}

function MemberEventsPage({ chamberName }: { chamberName: string }) {
  const query = useGetMyCmpMemberEventsQuery({ fetchPolicy: 'network-only', context: operationContext });
  const [editing, setEditing] = useState<Registration>();
  const [viewing, setViewing] = useState<Registration>();
  const data = query.data?.getMyCmpMemberEvents;
  return (
    <>
      <MemberEventsView chamberName={chamberName} eventsUrl={data?.eventsPageUrl} upcoming={data?.upcoming || []} past={data?.past || []}
        loading={query.loading && !data} error={query.error ? errorMessage(query.error, 'Your events could not be loaded.') : undefined}
        onRetry={() => void query.refetch()} onEdit={setEditing} onViewPast={setViewing} />
      {editing && <AttendeeDetailsModal row={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); void query.refetch(); }} />}
      {viewing && <PastAttendeesModal row={viewing} onClose={() => setViewing(undefined)} />}
    </>
  );
}
